<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import ElCurrencyInput from '@/components/common/ElCurrencyInput.vue'
import { useNBPTThresholds } from '@/composables/eos/nbpt-thresholds'
import { currencyFormatter } from '@/helpers/ep/formatters'
import { ISOs } from '@/types/core/Site'
import type { INBPTThresholdFilters } from '@/types/eos/filters'
import type { INBPTForm } from '@/types/eos/forms'
import { DEFAULT_PAGE_SIZES, DEFAULT_PAGINATION_LAYOUT } from '@/types/shared/pagination'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import type { FormInstance, FormRules } from 'element-plus'
import { reactive, ref } from 'vue'

const createAlertDialogVisible = ref<boolean>(false)
const addAlertFormRef = ref<FormInstance>()

const filters = reactive<INBPTThresholdFilters>({
  page: 1,
  per_page: DEFAULT_PAGE_SIZES[2],
})

const { getNBPTThresholdsQuery, createNBPTThresholdMutation, deleteNBPTThresholdMutation } = useNBPTThresholds()

const { data, isLoading } = getNBPTThresholdsQuery(filters)

const addAlertForm = reactive({
  iso: undefined,
  date: new Date(),
  price: undefined,
})

const addAlertFormRules = reactive<FormRules>({
  iso: [{ required: true, message: 'ISO is required', trigger: ['blur', 'change'] }],
  date: [{ required: true, message: 'Date is required', trigger: ['blur', 'change'] }],
  price: [{ required: true, message: 'Price is required', trigger: ['blur', 'change'] }],
})

const openAddAlertDialog = (formEl: FormInstance | undefined) => {
  formEl?.resetFields()
  createAlertDialogVisible.value = true
}

const handleAddAlert = async (formEl: FormInstance | undefined) => {
  if (!formEl) {
    return
  }

  await formEl.validate(async (valid) => {
    if (!valid) return

    createAlertDialogVisible.value = false

    const payload: INBPTForm = {
      price_usd: addAlertForm.price!,
      month: addAlertForm.date.getMonth() + 1,
      year: addAlertForm.date.getFullYear(),
      iso: addAlertForm.iso!,
    }

    await createNBPTThresholdMutation.mutateAsync(payload)
  })
}
</script>

<template>
  <ViewLayout class="nbpt-alerts">
    <template #controls>
      <el-button type="primary" @click="openAddAlertDialog(addAlertFormRef)">
        Add Net Benefit Price Threshold
      </el-button>
    </template>
    <el-dialog v-model="createAlertDialogVisible" title="Create NBPT Threshold" width="360" :show-close="false">
      <el-form
        ref="addAlertFormRef"
        :model="addAlertForm"
        :rules="addAlertFormRules"
        label-width="120"
        label-position="left"
      >
        <el-form-item prop="iso" label="ISO" required>
          <el-select v-model="addAlertForm.iso" style="width: 100%" placeholder="Select ISO" clearable filterable>
            <el-option v-for="iso in Object.values(ISOs)" :key="iso" :label="iso" :value="iso" />
          </el-select>
        </el-form-item>
        <el-form-item prop="date" label="Date" required>
          <el-date-picker v-model="addAlertForm.date" placeholder="Select month" type="month" />
        </el-form-item>
        <el-form-item prop="price" label="Price" required>
          <ElCurrencyInput v-model="addAlertForm.price" clearable />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="createAlertDialogVisible = false">Cancel</el-button>
          <el-button type="primary" @click="handleAddAlert(addAlertFormRef)"> Save </el-button>
        </span>
      </template>
    </el-dialog>
    <el-table v-loading="isLoading" :data="data?.data" empty-text="No thresholds found." style="width: 100%">
      <el-table-column prop="iso" label="ISO" />
      <el-table-column prop="date" label="Month">
        <template #default="scope">
          <span>{{
            new Date(scope.row.year, scope.row.month - 1).toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })
          }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="price_usd" label="Price" :formatter="currencyFormatter" />
      <el-table-column :min-width="80" align="right">
        <template #default="scope">
          <el-button link>
            <Icon
              icon="material-symbols:delete-outline"
              :height="20"
              @click="
                DeleteConfirmationModal(
                  'Delete NBPT Threshold',
                  'Are you sure you want to delete this threshold? This action cannot be undone',
                  scope.row.id,
                  deleteNBPTThresholdMutation.mutateAsync
                )
              "
            />
          </el-button>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination
      v-if="data && data.total_pages > 0"
      v-model:current-page="filters.page"
      v-model:page-size="filters.per_page"
      :disabled="isLoading"
      :layout="DEFAULT_PAGINATION_LAYOUT"
      :page-sizes="DEFAULT_PAGE_SIZES"
      :page-count="data.total_pages"
      :total="data.total_records"
      background
      class="self-end"
    />
  </ViewLayout>
</template>
